import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { type ReactElement } from 'react';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { CheckboxPopover } from '@/ui/composites/filters/CheckboxPopover';
import { Box, Cancel, IconButton, Text, Tooltip, useTheme } from '@/ui/primitives';
import { TABLE_PANEL_HEADER_HEIGHT } from './tableConstants';
import { TableSortPopover } from './TableSortPopover';
import { buildToolbarIconButtonSx, buildToolbarIconCircleSx } from './tableToolbar.helpers';
import { type TableToolbarProps } from './tableToolbar.types';
import { useTableToolbarState } from './useTableToolbarState';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';

const CLONE_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;

export const TableToolbar = <TSortKey extends string, TStatus extends string>({
  title,
  searchAriaLabel: _searchAriaLabel,
  searchValue,
  onDebouncedSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  sortOptions,
  onSortChange,
  filterStatuses,
  statusOptions: _statusOptions,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  entityOptions: _entityOptions,
  entityLabel: _entityLabel,
  filterSuites = [],
  onFilterSuitesChange,
  suiteOptions: _suiteOptions,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  previewFromDate = null,
  previewToDate = null,
  onFromDateChange,
  onToDateChange,
  availableTags: _availableTags,
  includeLatestQuickFilter: _includeLatestQuickFilter = false,
  statusFilterLabel: _statusFilterLabel = 'Latest Status',
  summaryCount = null,
  summarySingularLabel,
  summaryPluralLabel,
}: TableToolbarProps<TSortKey, TStatus>): ReactElement => {
  const mandatoryColumnVisibilityModel = columnOptions.reduce<Record<string, true>>(
    (acc, option) => {
      if (option.mandatory) {
        acc[option.field] = true;
      }
      return acc;
    },
    {},
  );
  const theme = useTheme();
  const toolbarControlSize = 32;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const {
    popoverState,
    hoverTarget,
    hasNonDefaultColumns,
    hasActiveSort,
    dateRangeLabel,
    setHoverTarget,
    openPopover,
    closePopover,
    preserveWindowScroll,
  } = useTableToolbarState({
    searchValue,
    onDebouncedSearchChange,
    columnVisibilityModel,
    defaultColumnVisibilityModel,
    columnOptions,
    onColumnVisibilityModelChange,
    sortKey,
    sortDirection,
    defaultSortKey,
    defaultSortDirection,
    onSortChange,
    filterStatuses,
    onFilterStatusesChange,
    filterEntities,
    onFilterEntitiesChange,
    filterSuites,
    onFilterSuitesChange,
    filterTags,
    onFilterTagsChange,
    dateQuickFilter,
    onDateQuickFilterChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    summaryCount,
    summarySingularLabel,
    summaryPluralLabel,
    previewFromDate,
    previewToDate,
  });
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const toolbarIconCircleSx = buildToolbarIconCircleSx;
  const toolbarIconTooltipDelayMs = 250;
  const hasActiveTableToolbarState = hasNonDefaultColumns || hasActiveSort;
  const resetTableToolbar = (): void => {
    preserveWindowScroll(() => {
      onSortChange(defaultSortKey, defaultSortDirection);
      onColumnVisibilityModelChange({
        ...defaultColumnVisibilityModel,
        ...mandatoryColumnVisibilityModel,
      });
      closePopover();
    });
  };
  return (
    <Stack
      direction="row"
      justifyContent={title || dateRangeLabel ? 'space-between' : 'flex-end'}
      alignItems="center"
      gap="md"
      sx={{
        width: '100%',
        minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
        height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
      }}
    >
      {title || dateRangeLabel ? (
        <Box
          sx={{
            minHeight: `${toolbarControlSize}px`,
            display: 'flex',
            alignItems: 'center',
            pl: s('sm'),
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          {title ? (
            <Text
              role="bodyStrong"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.h6.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.h6.lineHeight,
              }}
            >
              {title}
            </Text>
          ) : dateRangeLabel ? (
            <Text
              role="body"
              sx={{
                color: 'text.secondary',
                fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
              }}
            >
              {dateRangeLabel}
            </Text>
          ) : null}
        </Box>
      ) : null}
      <Stack
        direction="row"
        alignItems="center"
        gap="sm"
        sx={{
          minWidth: 0,
          height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          boxSizing: 'border-box',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          title="Columns"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open columns"
            onClick={openPopover('columns')}
            onMouseEnter={() => {
              setHoverTarget('columns');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'columns')}>
              <ViewColumnOutlinedIcon
                sx={{
                  color: hasNonDefaultColumns ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Sort"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <IconButton
            size="medium"
            aria-label="Open sort"
            onClick={openPopover('sort')}
            onMouseEnter={() => {
              setHoverTarget('sort');
            }}
            onMouseLeave={() => {
              setHoverTarget(null);
            }}
            sx={[
              toolbarIconButtonSx,
              { color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor },
            ]}
          >
            <Box sx={toolbarIconCircleSx(hoverTarget === 'sort')}>
              <SortOutlinedIcon
                sx={{
                  color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Reset"
          arrow
          enterDelay={toolbarIconTooltipDelayMs}
          slotProps={CLONE_TOOLTIP_SLOT_PROPS}
        >
          <Box
            component="span"
            sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
          >
            <IconButton
              size="medium"
              aria-label="Reset filters"
              onClick={resetTableToolbar}
              disabled={!hasActiveTableToolbarState}
              sx={[
                toolbarIconButtonSx,
                hasActiveTableToolbarState ? { color: 'primary.main' } : {},
              ]}
            >
              <Box sx={toolbarIconCircleSx(hoverTarget === 'reset' && hasActiveTableToolbarState)}>
                <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
              </Box>
            </IconButton>
          </Box>
        </Tooltip>
      </Stack>
      <CheckboxPopover
        open={popoverState.type === 'columns'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        heading="Columns"
        headingIcon={
          <ViewColumnOutlinedIcon sx={{ width: '18px !important', height: '18px !important' }} />
        }
        minWidth={300}
        sections={[
          {
            key: 'columns',
            options: columnOptions.map((option) => {
              const checked = option.mandatory ? true : columnVisibilityModel[option.field];
              return {
                key: option.field,
                label: option.label,
                checked,
                disabled: Boolean(option.mandatory),
                onChange: (nextChecked: boolean) => {
                  if (option.mandatory) return;
                  preserveWindowScroll(() => {
                    onColumnVisibilityModelChange({
                      ...columnVisibilityModel,
                      [option.field]: nextChecked,
                      ...mandatoryColumnVisibilityModel,
                    });
                  });
                },
              };
            }),
          },
        ]}
      />
      <TableSortPopover
        open={popoverState.type === 'sort'}
        anchorEl={popoverState.anchorEl}
        onClose={closePopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Stack>
  );
};

export type { CloneDateQuickFilter };
